<template>
  <v-layout row wrap>
    <v-flex xs6>
      <v-form class="px-2">
        <!-- Rule Collection For -->
        <v-flex xs4>
          <v-select v-model="ruleCollectionInfo.ruleCollectionFor" :label="messages.label.FOR" :disabled="isEditMode"
            item-text="title" item-value="value" :items="ruleCollectionForList" @change="updateAddRuleSetsFilters">
          </v-select>
        </v-flex>
        <template v-if="ruleCollectionInfo.ruleCollectionFor === 'account'">
          <div v-if="IS_POLARIS">
            <!-- Provider Group -->
            <v-flex sm4>
              <v-autocomplete v-model="selectedProviderGroup" :label="messages.label.PROVIDER_GROUP" item-text="title"
                item-value="title" :disabled="isEditMode" :items="providerGroups" :loading="loadingProviderGroups"
                return-object @change="retrieveSecondaryOwners(selectedProviderGroup.id)">
                <template v-slot:no-data>
                  <v-list-tile>
                    <v-list-tile-title>
                      No Provider groups
                    </v-list-tile-title>
                  </v-list-tile>
                </template>
              </v-autocomplete>
            </v-flex>
            <!-- Provider -->
            <v-flex xs4>
              <v-autocomplete v-model="selectedSecondaryOwner" :label="messages.label.PROVIDER_NAME"
                :loading="loadingSecondaryOwner" item-text="title" item-value="title" return-object
                @change="updateAddRuleSetsFilters" :items="secondaryOwners">
              </v-autocomplete>
            </v-flex>
          </div>
          <div v-else>
            <!-- Sub Account -->
            <v-flex sm4 v-if="mpxSubAccounts.length > 0">
              <v-autocomplete v-model="selectedSubAccount" :label="messages.label.SUB_ACCOUNT" item-text="title"
                item-value="title" :loading="loadingSubAccounts" :disabled="isEditMode" :items="mpxSubAccounts"
                return-object>
              </v-autocomplete>
            </v-flex>
            <!-- Sub Account Title-->
            <v-flex xs4 v-else>
              <v-text-field v-model="subAccountTitle" :clearable="!isEditMode" v-validate="'required|max:100'"
                :counter="100" :disabled="isEditMode" :label="messages.label.SUB_ACCOUNT" required>
              </v-text-field>
            </v-flex>
          </div>
        </template>
        <!-- Rule Collection Type -->
        <v-flex xs4>
          <v-select v-model="ruleCollectionInfo.type" item-text="title" item-value="value" :disabled="isEditMode"
            @change="updateAddRuleSetsFilters(); handleRuleCollectionTypeChange()"
            :label="messages.label.RULE_COLLECTION_TYPE" :items="ruleCollectionTypeList"></v-select>
        </v-flex>
        <!-- Rule Collection Title -->
        <v-flex xs10>
          <v-text-field v-model="ruleCollectionInfo.title" clearable v-validate="'required|max:100'"
            :error-messages="errors.collect('title')" data-vv-name="title" :counter="100"
            :label="messages.label.RULE_TITLE" required>
          </v-text-field>
        </v-flex>
        <!-- Rule Collection Description -->
        <v-flex xs10>
          <v-textarea v-model="ruleCollectionInfo.description" clearable v-validate="'max:100'"
            :error-messages="errors.collect('description')" data-vv-name="description" :counter="500"
            :label="messages.label.RULE_DESCRIPTION" required>
          </v-textarea>
        </v-flex>
        <!-- Rule Collection Enabled -->
        <v-flex xs4>
          <span class="subheading">{{ messages.label.ENABLED }}</span>
          <v-switch v-model="ruleCollectionEnabled" color="primary" class="mt-0"
            :label="`${ruleCollectionEnabled.toString()}`"></v-switch>
        </v-flex>
      </v-form>
    </v-flex>
  </v-layout>

</template>
<script>
import { mapState, mapActions, mapGetters, mapMutations } from "vuex"
import messages from "@/MetadataRules/messages.json"
import MetadataRulesService from "@/MetadataRules/MetadataRulesService"
import * as metadataRulesDataService from "@/MetadataRules/MetadataRulesDataService"
import { IS_POLARIS, VOD_SYCN_PREINGEST_DATA_SERVICE_URL } from "@/store/constants/getter-types"
import { SECONDARY_OWNER_ANY } from "@/Shared/constants"
import { GET_PROVIDER_GROUPS, GET_MPX_SUB_ACCOUNTS } from "@/store/constants/action-types"
import { SET_ADD_RULE_SETS_FILTERS, SET_RULECOLLECTION_RULESETS } from "@/store/constants/mutation-types"

export default {
  name: "RuleCollectionInfo",
  $_veeValidate: {
    validator: "new"
  },
  computed: {
    ...mapState([
      "mpxParentAccount", "providerGroups", "mpxSubAccounts"
    ]),
    ...mapGetters([
      IS_POLARIS, VOD_SYCN_PREINGEST_DATA_SERVICE_URL
    ])
  },
  data() {
    return {
      ruleCollectionForList: MetadataRulesService.getForList(),
      ruleCollectionTypeList: MetadataRulesService.getRuleTypeList(),
      isEditMode: false,
      ruleCollectionInfo: {
        ruleCollectionFor: "global",
        type: "VALIDATION",
        ownerId: "",
        secondaryOwner: "",
        title: "",
        description: "",
        enabled: true
      },
      loadingProviderGroups: false,
      selectedProviderGroup: {},
      loadingSecondaryOwner: false,
      selectedSubAccount: {},
      subAccountTitle: "",
      loadingSubAccounts: false,
      secondaryOwners: [],
      selectedSecondaryOwner: {}, // providers are secondary owners
      ruleCollectionEnabled: true, // NOTE: Using this outside of "ruleCollectionInfo" as binding is not working
      dictionary: this.getValidationDictionary()
    }
  },
  methods: {
    ...mapActions([
      GET_PROVIDER_GROUPS, GET_MPX_SUB_ACCOUNTS
    ]),
    ...mapMutations("metadataRules", [
      SET_ADD_RULE_SETS_FILTERS,
      SET_RULECOLLECTION_RULESETS
    ]),

    /**
     * getValidationDictionary - Validation messages for vee-validate
     * @returns {object}
     */
    getValidationDictionary() {
      return {
        custom: {
          title: {
            required: () => "Title can't be empty",
            max: "Title may not be greater than 100 characters"
          },
          description: {
            max: "Description may not be greater than 500 characters"
          }
        }
      }
    },

    /**
     * resetData - Reset rule info data
     */
    resetData() {
      this.ruleCollectionInfo = {
        ruleCollectionFor: "global",
        type: "VALIDATION",
        ownerId: "",
        secondaryOwner: "",
        title: "",
        description: "",
        enabled: true
      }
      this.ruleCollectionEnabled = true
      this.isEditMode = false
    },

    /**
     * resetRuleCollectionInfo - Reset rule collection data and form
     */
    resetRuleCollectionInfo() {
      this.resetData()
      this.$nextTick(() => {
        this.$validator.reset()
      })
    },

    /**
     * loadRuleCollectionInfo - update view with rule collection data
     * @param {object} ruleCollectionData
     */
    loadRuleCollectionInfo(ruleCollectionData) {
      this.resetRuleCollectionInfo()
      if (!this.$lodash.isUndefined(ruleCollectionData)) {
        if (this.IS_POLARIS) {
          if (this.isOwnerParent(ruleCollectionData.ownerId)) {
            this.ruleCollectionInfo.ruleCollectionFor = "global"
          } else {
            this.ruleCollectionInfo.ruleCollectionFor = "account"
            this.retrieveProviderGroups(ruleCollectionData.ownerId, ruleCollectionData.secondaryOwner)
          }
        } else {
          if (this.isOwnerParent(ruleCollectionData.ownerId)) {
            if (ruleCollectionData.secondaryOwner !== SECONDARY_OWNER_ANY) {
              this.ruleCollectionInfo.ruleCollectionFor = "account"
              this.subAccountTitle = ruleCollectionData.secondaryOwner
            } else {
              this.ruleCollectionInfo.ruleCollectionFor = "global"
            }
          } else {
            this.ruleCollectionInfo.ruleCollectionFor = "account"
            this.retrieveSubAccounts(ruleCollectionData.ownerId)
          }
        }

        this.isEditMode = true
        this.ruleCollectionInfo.type = ruleCollectionData.type
        this.ruleCollectionInfo.title = ruleCollectionData.title
        this.ruleCollectionInfo.ownerId = ruleCollectionData.ownerId
        this.ruleCollectionInfo.secondaryOwner = ruleCollectionData.secondaryOwner
        this.ruleCollectionInfo.description = ruleCollectionData.description
        this.ruleCollectionInfo.enabled = !ruleCollectionData.disabled
      } else {
        this.isEditMode = false
        this.retrieveProviderGroups()
      }
    },

    /**
     * isOwnerParent - Check if the rule's owner is parent account
     */
    isOwnerParent(ownerId) {
      return this.mpxParentAccount.id === ownerId
    },

    /**
     * retrieveProviderGroups - retrieve provider groups
     */
    retrieveProviderGroups(currentProviderGroupAccountId, currentSecondaryOwner) {
      // If the account is not Polaris, just return empty array
      if (!this.IS_POLARIS) {
        return
      }
      this.loadingProviderGroups = true
      this.GET_PROVIDER_GROUPS()
        .then(() => {
          if (!this.$lodash.isUndefined(currentProviderGroupAccountId)) {
            this.selectedProviderGroup = this.$lodash.find(this.providerGroups, { "ownerId": currentProviderGroupAccountId })
          } else {
            this.selectedProviderGroup = this.providerGroups[0]
          }
          return this.retrieveSecondaryOwners(this.selectedProviderGroup.id, currentSecondaryOwner)
        })
        .catch(() => {
          // TODO: handle error
        }).finally(() => {
          this.loadingProviderGroups = false
        })
    },

    /**
     * retrieveSubAccounts - retrieve provider groups
     */
    retrieveSubAccounts(currentSubAccountId) {
      this.loadingSubAccounts = true
      this.GET_MPX_SUB_ACCOUNTS()
        .then(() => {
          if (!this.$lodash.isUndefined(currentSubAccountId)) {
            this.selectedSubAccount = this.$lodash.find(this.mpxSubAccounts, { "id": currentSubAccountId })
          } else {
            this.selectedSubAccount = this.mpxSubAccounts[0]
          }
        })
        .catch(() => {
          // TODO: handle error
        }).finally(() => {
          this.loadingSubAccounts = false
        })
    },

    /**
     * retrieveSecondaryOwners - Retrieve all providers from API service
     * @param {string} selectedSecondaryOwners
     */
    retrieveSecondaryOwners(providerGroupId, currentSecondaryOwner) {
      this.updateAddRuleSetsFilters()
      let queryObject = {}
      if (!this.$lodash.isUndefined(providerGroupId)) {
        queryObject["byProviderGroupId"] = providerGroupId
      }
      this.loadingSecondaryOwner = true
      return metadataRulesDataService
        .retrieveSecondaryOwners(queryObject,
          this.VOD_SYCN_PREINGEST_DATA_SERVICE_URL)
        .then((secondaryOwners) => {
          this.secondaryOwners = secondaryOwners
          if (!this.$lodash.isUndefined(currentSecondaryOwner)) {
            if (currentSecondaryOwner === SECONDARY_OWNER_ANY) {
              this.selectedSecondaryOwner = this.secondaryOwners[0]
            } else {
              this.selectedSecondaryOwner = this.$lodash.find(this.secondaryOwners, { "title": currentSecondaryOwner })
            }
          } else {
            this.selectedSecondaryOwner = this.secondaryOwners[0]
          }
        })
        .catch(() => {
          // TODO: handle error
        }).finally(() => {
          this.loadingSecondaryOwner = false
        })
    },

    /**
     * validate - Validate
     */
    validate() {
      this.$validator.validateAll()
        .then((result) => {
          this.ruleCollectionInfo.enabled = this.ruleCollectionEnabled // Please see note at the definition of this property in "data"
          if (this.$lodash.isNull(this.ruleCollectionInfo.description)) {
            this.ruleCollectionInfo.description = ""
          }
          // Update owner Id
          if (this.ruleCollectionInfo.ruleCollectionFor === "global") {
            this.ruleCollectionInfo.ownerId = this.mpxParentAccount.id
            this.ruleCollectionInfo.secondaryOwner = SECONDARY_OWNER_ANY
          } else {
            if (this.IS_POLARIS) {
              this.ruleCollectionInfo.ownerId = this.selectedProviderGroup.ownerId
              if (this.selectedSecondaryOwner.title === "Any") {
                this.ruleCollectionInfo.secondaryOwner = SECONDARY_OWNER_ANY
              } else {
                this.ruleCollectionInfo.secondaryOwner = this.selectedSecondaryOwner.title
              }
            } else {
              if (this.$lodash.isEmpty(this.selectedSubAccount)) {
                this.ruleCollectionInfo.ownerId = this.mpxParentAccount.id
                if (this.$lodash.isEmpty(this.subAccountTitle.trim())) {
                  this.ruleCollectionInfo.secondaryOwner = SECONDARY_OWNER_ANY
                } else {
                  this.ruleCollectionInfo.secondaryOwner = this.subAccountTitle
                }
              } else {
                this.ruleCollectionInfo.ownerId = this.selectedSubAccount.id
                this.ruleCollectionInfo.secondaryOwner = SECONDARY_OWNER_ANY
              }
            }
          }
          MetadataRulesService.$emit("event:ruleCollectionInfoValidation", result, this.ruleCollectionInfo)
        })
    },

    /**
     * updateAddRuleSetsFilters - Update to ensure that 'Add Rulesets' fetches correct data
     */
    updateAddRuleSetsFilters() {
      let addRuleSetsFilters = {
        ruleCollectionFor: this.ruleCollectionInfo.ruleCollectionFor,
        ruleCollectionType: this.ruleCollectionInfo.type,
        providerGroupAccountId: this.selectedProviderGroup.ownerId
      }
      this.SET_ADD_RULE_SETS_FILTERS(addRuleSetsFilters)
      MetadataRulesService.$emit("event:addRuleSetsFilterUpdated")
    },
    /**
    * handleRuleCollectionTypeChange - Update the RulesSetRulesList when user change the ruleType
    */
    handleRuleCollectionTypeChange() {
      let ruleCollectionRuleSetsList = []
      this.SET_RULECOLLECTION_RULESETS(ruleCollectionRuleSetsList)
      MetadataRulesService.$emit("event:ruleCollectionTypeChanged", this.ruleCollectionInfo.type)
    }
  },
  mounted() {
    // Validator initialization
    this.$validator.localize("en", this.dictionary)
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    this.updateAddRuleSetsFilters()

    /**
     * Events
     */
    MetadataRulesService.$on("event:ruleCollectionDataRetrieved", this.loadRuleCollectionInfo)
    MetadataRulesService.$on("event:validateRuleCollectionInfo", this.validate)
  },
  beforeDestroy() {
    MetadataRulesService.$off("event:validateRuleCollectionInfo", this.validate)
    MetadataRulesService.$off("event:ruleCollectionDataRetrieved", this.loadRuleCollectionInfo)
  }
}
</script>
<style lang="sass">

</style>



// WEBPACK FOOTER //
// src/MetadataRules/RuleCollection/RuleCollectionInfo.vue